import {
    useCallback, useState, useMemo,
} from 'react'
import {
    TimePicker as MUITimePicker,
    LocalizationProvider,
} from '@mui/x-date-pickers-pro'
import moment, {
    Moment,
} from 'moment'
import {
    AdapterMoment,
} from '@mui/x-date-pickers-pro/AdapterMoment'
import {
    ReactComponent as TimePickerIcon,
} from 'app/shared-components/images/icons/timepicker.svg'
import Label from 'app/shared-components/Label'
import useStyles from './TimePicker.style'

const defaultView: ('hours' | 'minutes' | 'seconds')[] = [
    'hours',
    'minutes',
]

export default function TimePicker({
    value,
    onChange,
    title,
    disabled = false,
    required = false,
    disableFuture = false,
    disablePast = false,
    className,
    views = defaultView,
    defaultValue = '00:00:00.000',
}: Readonly<{
    value: string;
    onChange: (value: string) => void;
    title: string;
    disabled?: boolean;
    required?: boolean;
    disableFuture?: boolean;
    disablePast?: boolean;
    className?: string;
    views?: ('hours' | 'minutes' | 'seconds')[];
    defaultValue?: string;
}>) {
    const [
        open,
        setOpen,
    ] = useState<boolean>(false)
    const {
        classes, cx,
    } = useStyles({
        required,
        disabled,
    })
    const parsedValue = useMemo(() => {
        const result = (value || defaultValue)?.split(':')?.slice(0, 3)

        return moment({
            hours: parseInt(result?.[0], 10) || 0,
            minutes: parseInt(result?.[1], 10) || 0,
            seconds: parseInt(result?.[2], 10) || 0,
        })
    }, [
        value,
        defaultValue,
    ])

    const handleChange = useCallback(
        (val: Moment) => {
            if (moment(val).isValid()) {
                onChange(moment(val).format('HH:mm:ss.SSS'))
            }
        },
        [onChange],
    )

    const handleAccept = useCallback((val: Moment) => {
        handleChange(val)
        setOpen(false)
    }, [handleChange])

    const onClick = useCallback(() => {
        setOpen(!open)
    }, [open])

    return (
        <div className={cx(classes.timePicker, className)}>
            <Label title={title} />
            <LocalizationProvider
                dateAdapter={AdapterMoment}
                adapterLocale="en-gb"
            >
                <MUITimePicker
                    open={open}
                    ampm={false}
                    closeOnSelect
                    autoFocus
                    disabled={disabled}
                    disableFuture={disableFuture}
                    disablePast={disablePast}
                    onChange={handleChange}
                    onAccept={handleAccept}
                    value={parsedValue}
                    views={views}
                    slotProps={{
                        textField: {
                            required,
                            onClick,
                            className: classes.textField,
                            disabled,
                            variant: 'standard',
                            InputProps: {
                                endAdornment: <TimePickerIcon onClick={onClick} />,
                            },
                            inputProps: {
                                sx: {
                                    padding: '4px 0 2px',
                                },
                            },
                        },
                        actionBar: {
                            onAccept: () => {
                                setOpen(false)
                            },
                            sx: {
                                justifyContent: 'center !important',
                            },
                        },
                    }}
                />
            </LocalizationProvider>
        </div>
    )
}
